import {
  CustomMappingType,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand } from '@principle-theorem/principle-core';
import { ISterilisationMachine } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  INamedDocument,
  WithRef,
  XSLXImporterExporter,
  asyncForEach,
  nameSorter,
  snapshot,
  toNamedDocument,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import {
  BaseSourceToXSLX,
  IBasicMigrationRecord,
  XSLXToBasicRecord,
} from './basic-source-to-xslx';

export const STERILISATION_MACHINE_RESOURCE_TYPE = 'sterilisationMachine';

export const STERILISATION_MACHINE_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Sterilisation Machine',
    description: `Use this to map the autoclave from the source PMS to those setup in Principle.`,
    type: STERILISATION_MACHINE_RESOURCE_TYPE,
    allowManualInput: true,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Sterilisation Machine',
    destinationIdentifier: 'Principle Sterilisation Machine',
  },
});

export const STERILISATION_MACHINE_HEADERS = [
  {
    key: 'label',
    header: 'Sterilisation Machine',
  },
  {
    key: 'mapTo',
    header: 'Principle Sterilisation Machine',
  },
];

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IBaseMigrationSterilisationMachine
  extends IBasicMigrationRecord {}

class SterilisationMachineXSLX extends BaseSourceToXSLX<
  ISterilisationMachine,
  IBaseMigrationSterilisationMachine
> {
  headers = STERILISATION_MACHINE_HEADERS;
}

class XSLXToSterilisationMachine extends XSLXToBasicRecord {
  headers = STERILISATION_MACHINE_HEADERS;
}

export abstract class SterilisationMachineMappingHandler<
  SterilisationMachineRecord extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<SterilisationMachineRecord>,
> extends BaseCustomMappingHandler<ISterilisationMachine> {
  customMapping = STERILISATION_MACHINE_MAPPING;
  abstract sourceEntity: SourceEntity;
  abstract translateFn: (
    record: SterilisationMachineRecord['data']
  ) => IBaseMigrationSterilisationMachine;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const machine = this.translateFn(record);
        return {
          label: machine.name,
          value: this.sourceEntity.getSourceRecordId(record).toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ISterilisationMachine>[]> {
    const machines = await Firestore.getDocs(
      Brand.sterilisationMachineCol({ ref: migration.configuration.brand.ref })
    );
    return sortBy(
      machines.map((machine) => toNamedDocument(machine)),
      nameSorter()
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, machines, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, machines, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, machines, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().download(fileName, machines, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToSterilisationMachine()
    );
    const translationMap = new TranslationMapHandler(migration);

    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const matchingOption = sourceOptions.find(
        (option) => option.label === item.label
      );
      if (!matchingOption || !item.mapTo) {
        return;
      }

      const foundMachine = destinationOptions.find(
        (option) => option.name === item.mapTo
      );
      if (!foundMachine) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find equipment for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
          reason: `Destination identifier "${item.mapTo.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          destinationIdentifier: foundMachine.ref,
          sourceIdentifier: matchingOption.value,
          destinationValue: { value: item.mapTo },
          sourceLabel: matchingOption.label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    machines: IBaseMigrationSterilisationMachine[];
    translator: SterilisationMachineXSLX;
  }> {
    const fileName = this.getFileName();
    const machines = await this._getSourceMachines(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new SterilisationMachineXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap)
    );
    return { fileName, machines, translator };
  }

  private async _getSourceMachines(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationSterilisationMachine[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();

    return sortBy(
      records.map((record) => this.translateFn(record.data)),
      'name'
    );
  }
}
